// Response contract of the Power admin proxy GET /power/admin/opin/consent-lifecycle: the
// operator's read-only view of the OPIN receiver consent lifecycle. Power reads the receiver's
// own summary server-side, validates it against the receiver contract, and projects it onto this
// schema; the admin SPA depends on this contract only, never on a backend package. A drift test in
// services/power parses a full receiver summary through that projection.
//
// No personal data: a consent appears only by the tail of its id, a transmitter by its name.
import { Schema } from 'effect';
import { AuthorisationServerId, IsoDateTimeUtc } from '@finnest/core/effect';
import { Uuid } from '../../_shared/formats.ts';

const Count = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0));
const Instant = IsoDateTimeUtc;
// Same bounds as the receiver summary: enough of the id to find it at the transmitter, nothing more.
const CONSENT_TAIL_MAX = 8;
const LAST_ERROR_MAX = 64;
const ConsentTail = Schema.NonEmptyString.check(Schema.isMaxLength(CONSENT_TAIL_MAX));

// The receiver lists at most this many pending revocations and anomalies (receiver
// lifecycle/persistence/summary.ts LIST_LIMIT). A count taken from a full list is a lower bound.
export const OPIN_CONSENT_LIFECYCLE_LIST_CAP = 50;

/** The receiver's connection id; same brand as the receiver's own `ConnectionId`. */
export const OpinConnectionId = Uuid.pipe(Schema.brand('ConnectionId'));
export type OpinConnectionId = typeof OpinConnectionId.Type;

// Why the receiver sweep handed a live connection to the operator (receiver CONSENT_ANOMALIES).
export const OpinConsentAnomaly = Schema.Literals([
  'unexpected_rejection',
  'unexpected_status',
  'not_found',
]);
export type OpinConsentAnomaly = typeof OpinConsentAnomaly.Type;

// The structs below are closed contracts: decode them with `{ onExcessProperty: 'error' }`, so a
// receiver field outside this contract (a full consent id, personal data) fails the response
// instead of reaching the browser.
export const OpinPendingRevocation = Schema.Struct({
  connectionId: OpinConnectionId,
  authorisationServerId: AuthorisationServerId,
  authorisationServerName: Schema.NonEmptyString,
  consentTail: ConsentTail,
  attempts: Count,
  lastError: Schema.NullOr(Schema.String.check(Schema.isMaxLength(LAST_ERROR_MAX))),
  firstFailedAt: Schema.NullOr(Instant),
  nextAttemptAt: Schema.NullOr(Instant),
  overdue: Schema.Boolean,
});
export type OpinPendingRevocation = typeof OpinPendingRevocation.Type;

export const OpinConsentAnomalyEntry = Schema.Struct({
  connectionId: OpinConnectionId,
  authorisationServerId: AuthorisationServerId,
  authorisationServerName: Schema.NonEmptyString,
  consentTail: ConsentTail,
  anomaly: OpinConsentAnomaly,
  since: Instant,
});
export type OpinConsentAnomalyEntry = typeof OpinConsentAnomalyEntry.Type;

export const OpinConsentSweep = Schema.Struct({
  ranAt: Instant,
  finishedAt: Instant,
  totals: Schema.Struct({
    expired: Count,
    authorizationTimedOut: Count,
    revoked: Count,
    revocationRetried: Count,
    reconciled: Count,
    detectedAtTransmitter: Count,
    anomalies: Count,
    failures: Count,
  }),
});
export type OpinConsentSweep = typeof OpinConsentSweep.Type;

// The receiver's per-status counts are projected onto the two the page shows; the lists are capped
// by the receiver, so `revocationPending` is the true count behind `pendingRevocations`. A list
// longer than the cap breaks the receiver contract and is rejected rather than rendered.
export const OpinConsentLifecycleSummary = Schema.Struct({
  generatedAt: Instant,
  active: Count,
  revocationPending: Count,
  expiringWithin7Days: Count,
  pendingRevocations: Schema.Array(OpinPendingRevocation).check(
    Schema.isMaxLength(OPIN_CONSENT_LIFECYCLE_LIST_CAP),
  ),
  anomalies: Schema.Array(OpinConsentAnomalyEntry).check(
    Schema.isMaxLength(OPIN_CONSENT_LIFECYCLE_LIST_CAP),
  ),
  lastSweep: Schema.NullOr(OpinConsentSweep),
}).annotate({
  title: 'PowerAdminConsentLifecycleSummary',
  description:
    'Operator view of the OPIN receiver consent lifecycle served by the Power admin proxy.',
});
export type OpinConsentLifecycleSummary = typeof OpinConsentLifecycleSummary.Type;
